import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, map, catchError, filter, switchMap, shareReplay } from 'rxjs/operators';
import { AppConfiguration } from 'src/app/core/models/config.models';

interface ConfigState {
  config: AppConfiguration | null;
  loading: boolean;
  error: any;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private configSubject = new BehaviorSubject<ConfigState>({
    config: null,
    loading: false,
    error: null
  });

  config$ = this.configSubject.asObservable();
  
  // Observable que emite solo cuando la configuración está cargada
  private configLoaded$: Observable<AppConfiguration>;

  constructor(private http: HttpClient) {
    // Crear el observable que se comparte entre todas las suscripciones
    this.configLoaded$ = this.config$.pipe(
      filter(state => !state.loading && state.config !== null),
      map(state => state.config!),
      shareReplay(1) // Cachea el último valor emitido
    );
    
    // Cargar la configuración automáticamente al inicializar el servicio
    this.loadConfig().subscribe();
  }

  loadConfig(): Observable<AppConfiguration> {
    // Si ya está cargada, retornarla
    const currentState = this.configSubject.value;
    if (currentState.config && !currentState.loading) {
      return of(currentState.config);
    }

    // Si ya se está cargando, esperar a que termine
    if (currentState.loading) {
      return this.configLoaded$;
    }

    this.configSubject.next({ ...currentState, loading: true });

    return this.http.get<AppConfiguration>('assets/config.json').pipe(
      tap(config => {
        //console.log('Configuración cargada:', config);
        this.configSubject.next({ config, loading: false, error: null });
      }),
      catchError(error => {
        console.error('Error loading configuration:', error);
        this.configSubject.next({ ...this.configSubject.value, loading: false, error });
        throw error;
      })
    );
  }

  getConfig(): Observable<AppConfiguration> {
    return this.configLoaded$;
  }

  updateConfig(updatedConfig: AppConfiguration): void {
    this.configSubject.next({ config: updatedConfig, loading: false, error: null });
  }

  getConfigValue(section: keyof AppConfiguration, key: string): Observable<any> {
    return this.configLoaded$.pipe(
      map(config => {
        //console.log('Accediendo a configuración:', { section, key, config });
        
        // Verificar si la sección existe
        if (!config || !config[section]) {
          console.warn(`Sección '${section}' no encontrada en la configuración`);
          return null;
        }

        const sectionData = config[section] as any;
        
        // Verificar si la clave existe en la sección
        if (!sectionData || !(key in sectionData)) {
          console.warn(`Clave '${key}' no encontrada en la sección '${section}'`);
          return null;
        }

        return sectionData[key];
      })
    );
  }

  // Método de conveniencia para obtener valores de configuración de forma síncrona
  // Solo usar cuando estés seguro de que la configuración ya está cargada
  getConfigValueSync(section: keyof AppConfiguration, key: string): any {
    const currentState = this.configSubject.value;
    if (currentState.config && currentState.config[section]) {
      const sectionData = currentState.config[section] as any;
      return sectionData[key] || null;
    }
    return null;
  }

  // Verificar si la configuración está cargada
  isConfigLoaded(): boolean {
    return this.configSubject.value.config !== null;
  }
}